/**
 * Decided colour literals and the tint recipes behind custom-theme.ts — pure data and
 * pure functions, nothing instantiated. A `{family.step}` string is an Aura token
 * reference the engine turns into `var(--p-family-step)`.
 */
export type Scheme = 'light' | 'dark';
export type Step = 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 950;

export const SEVERITY_FAMILY = {
  success: 'green',
  info: 'blue',
  warn: 'orange',
  danger: 'red',
} as const;
export type Severity = keyof typeof SEVERITY_FAMILY;
export type SeverityFamily = (typeof SEVERITY_FAMILY)[Severity];

/** Buttons alone carry a fifth severity; Aura's `help` fill fails AA, so it joins the recipes. */
export const BUTTON_FAMILY = { ...SEVERITY_FAMILY, help: 'purple' } as const;
export type ButtonSeverity = keyof typeof BUTTON_FAMILY;
export type Family = (typeof BUTTON_FAMILY)[ButtonSeverity];

export type TokenRef = `{${Family}.${Step}}`;
export const tokenRef = (family: Family, step: Step): TokenRef => `{${family}.${step}}`;

/** A per-scheme colour map holds Aura step references or hex literals, nothing else. */
type SchemeColors = Record<Scheme, Record<string, `{${string}.${Step}}` | `#${string}`>>;

/** The neutral grey decided once: the chart compare series. */
export const COMPARE_GREY = '#838da0';

// Review Bands colour-by hues 9 and 10 (GF-6463): brown and dark teal, because the chart
// cycle's pink reads as the fuchsia isolation hue and its indigo as hue 1's blue.
export const COLOR_BY_EXTRA = ['#a16207', '#115e59'] as const;

/** Prod's brand ramp (Figma brandRA): 600 is the brand blue, 700 / 800 its light hover and press. */
export const PRIMARY_RAMP = {
  50: '#e9f5ff',
  100: '#d6eeff',
  200: '#b5dcff',
  300: '#89c4ff',
  400: '#5b9cff',
  500: '#3575ff',
  600: '#0b3ef9',
  700: '#1638c4',
  800: '#122ca9',
  900: '#101d91',
  950: '#0c1e3d',
} as const;

/** Prod's light surface ramp (main's design tokens): slate with grey hairlines at 200 / 300. */
export const SURFACE_LIGHT = {
  0: '#ffffff',
  50: '#f6f8fb',
  100: '#eef2f6',
  200: '#eef2f6',
  300: '#e5e7eb',
  400: '#94a3b8',
  500: '#64748b',
  600: '#667085',
  700: '#334155',
  800: '#1f2937',
  900: '#181d27',
  950: '#181d27',
} as const;

/** Prod's --surface-border grey (#e5e7eb / #3f3f46): its chart grid lines and tooltip edges. */
export const SURFACE_BORDER = { light: '{surface.300}', dark: '{zinc.700}' } as const;

/**
 * Prod's dark surfaces: its override sheet re-pointed only 900 and 950, so 0-800 keep the light
 * ramp's slate under a near-black card (900) on a zinc ground (950).
 */
export const SURFACE_DARK = { ...SURFACE_LIGHT, 900: '#09090b', 950: '#18181b' } as const;

/** Prod's checkbox edge: its --border-color greys in light, --surface-border and a white wash in dark. */
export const CHECKBOX_EDGE = {
  light: { rest: '#e0e0e0', hover: '#bdbdbd' },
  dark: { rest: '{zinc.700}', hover: 'rgba(255, 255, 255, 0.2)' },
} as const;

/**
 * Develop's light muted grey, kept over prod's #64748b ink and #94a3b8 field-hover edge for its
 * AA margin (palette call, 2026-10-30), so the ramp above leaves both where they are.
 */
export const MUTED_LIGHT = '#5f6f85';

// Light slots 2 and 7 (orange, amber) blur under colour-vision deficiency (ΔE 1.6): charts label every series.
export const CATEGORICAL = {
  light: {
    1: '{blue.700}',
    2: '{orange.600}',
    3: '{green.800}',
    4: '{purple.900}',
    5: '{pink.700}',
    6: '{cyan.600}',
    7: '{amber.600}',
    8: '{purple.500}',
    other: '{slate.500}',
    compare: COMPARE_GREY,
  },
  dark: {
    1: '{blue.400}',
    2: '{orange.600}',
    3: '{green.400}',
    4: '{purple.500}',
    5: '{pink.400}',
    6: '{cyan.200}',
    7: '{amber.400}',
    8: '{purple.300}',
    other: '{zinc.500}',
    compare: '{zinc.500}',
  },
} as const satisfies SchemeColors;

type SeriesColor = `{${string}.${Step}}` | `#${string}`;

/** Prod's series colours in light, chart by chart; the shape every scheme repeats. */
const CHART_SERIES_LIGHT = {
  // Home "Margin over time" (pages/home/configs/net-realization-series.ts on prod).
  marginOverTime: {
    price: '#2563eb',
    cost: '#fb923c',
    revenue: '#10b981',
    profit: '#e5e7eb',
    crosshair: '#3b82f6',
  },
  // Insight-card charts, by the metric a dataset label names (shared/utils/insight-chart-mapping.ts
  // on prod).
  insight: {
    revenue: '#3b82f6',
    price: '#0ea5e9',
    cost: '#ef4444',
    margin: '#8b5cf6',
    profit: '#f97316',
    volume: '#14b8a6',
  },
  // Prod's insight fallback palette: pie slices, and series no metric names in turn after a
  // revenue, cost or unnamed STLY series has taken the first.
  insightCycle: {
    1: '#6366f1',
    2: '#ec4899',
    3: '#84cc16',
    4: '#f59e0b',
    5: '#06b6d4',
    6: '#a855f7',
  },
  // The combined two-metric insight chart with STLY: price's sky and volume's teal blur at chart
  // width, so there they take blue and amber.
  insightCombined: { price: '#3b82f6', volume: '#f59e0b' },
  // Pricing "Impact over time": the target line.
  impactOverTime: { target: '#64748b' },
  // Prod's `--chart-color-1..10`: the pricing charts, the funnel and the band-mix rows read it.
  chartCycle: {
    1: '#0b3ef9',
    2: '#f97316',
    3: '#10b981',
    4: '#8b5cf6',
    5: '#f43f5e',
    6: '#06b6d4',
    7: '#fbbf24',
    8: '#84cc16',
    9: '#ec4899',
    10: '#6366f1',
  },
} as const satisfies Record<string, Record<string, SeriesColor>>;

export type ChartSeriesShape = {
  readonly [C in keyof typeof CHART_SERIES_LIGHT]: {
    readonly [S in keyof (typeof CHART_SERIES_LIGHT)[C]]: SeriesColor;
  };
};

/**
 * The colours each chart paints its series on prod (origin/main 7284386b0). Prod is the
 * baseline, so a metric keeps its own chart's prod colour even where another chart paints the
 * same metric differently; a chart prod paints the same in both schemes repeats it in dark.
 */
export const CHART_SERIES: Readonly<Record<Scheme, ChartSeriesShape>> = {
  light: CHART_SERIES_LIGHT,
  dark: {
    ...CHART_SERIES_LIGHT,
    // Prod drew the target in its dark muted text.
    impactOverTime: { target: '#a1a1aa' },
  },
};

/** Prod's heatmap: its deeper light red end, gray.100 neutral, and its dark ramp (main's override sheet). */
export const HEATMAP = {
  light: { low: '#fa9696', mid: '{gray.100}', high: '{green.400}' },
  dark: { low: '{red.800}', mid: '{zinc.800}', high: '{green.800}' },
} as const satisfies SchemeColors;

export const BAND_COLORS = {
  light: {
    1: '#0b3ef9',
    2: '#ea580c',
    3: '#059669',
    4: '#8b5cf6',
    5: '#f43f5e',
    6: '#0891b2',
    other: '{slate.500}',
  },
  dark: {
    1: '#3575ff',
    2: '#ea580c',
    3: '#059669',
    4: '#8b5cf6',
    5: '#f43f5e',
    6: '#0891b2',
    other: '{zinc.500}',
  },
} as const satisfies SchemeColors;

/** An opaque hex or a translucent rgba() literal, for colours no Aura step spells. */
type ColorLiteral = `#${string}` | `rgba(${string})`;

export interface HighlightTokens {
  readonly background: ColorLiteral;
  readonly focusBackground: ColorLiteral;
  readonly color: ColorLiteral;
  readonly focusColor: ColorLiteral;
}

/**
 * The selection tint Aura gives the date range, the sorted header, the selected list option and
 * the light selected row (dark rows read `SELECTED_ROW_DARK`). The values test and prod ship,
 * lighter than the primary ramp's 100 step: a pale blue under brand ink in light, the brand
 * blue at 16% / 24% under white ink in dark.
 */
export const HIGHLIGHT = {
  light: {
    background: '#e9f5ff',
    focusBackground: '#d6eeff',
    color: '#1638c4',
    focusColor: '#122ca9',
  },
  dark: {
    background: 'rgba(11, 62, 249, .16)',
    focusBackground: 'rgba(11, 62, 249, .24)',
    color: 'rgba(255,255,255,.87)',
    focusColor: 'rgba(255,255,255,.87)',
  },
} as const satisfies Record<Scheme, HighlightTokens>;

/**
 * Prod's dark selected-row fill, a lighter blue than the dark highlight. The datatable row
 * reads it in place of `HIGHLIGHT.dark.background`; light rows and the row ink keep the highlight.
 */
export const SELECTED_ROW_DARK = 'rgba(53, 117, 255, .16)' satisfies ColorLiteral;

export interface NavColors {
  readonly card: ColorLiteral; // tenant card fill
  readonly logo: ColorLiteral; // tenant logo box glyph
  readonly iconBox: ColorLiteral; // tenant logo box text
  readonly active: ColorLiteral; // current page and open group pill, collapsed focus ring
  readonly activeHover: ColorLiteral;
  readonly accent: ColorLiteral; // building icon in the tenant list rows
  readonly chevron: ColorLiteral; // tenant select trigger on the card
  readonly optionBorder: ColorLiteral; // logo box edge in the tenant list rows
}

/**
 * The side nav as test and prod paint it, where that differs from the shared ramp: their
 * stylesheet named these as literals before the primary ramp moved, so the nav keeps them.
 */
export const NAV = {
  light: {
    card: '#1638c4',
    logo: '#1638c4',
    iconBox: '#5b9cff',
    active: '#0b3ef9',
    activeHover: '#0929cc',
    accent: '#0b3ef9',
    chevron: '#bdbdbd',
    optionBorder: '#e0e0e0',
  },
  dark: {
    card: '#1638c4',
    logo: '#1638c4',
    iconBox: '#5b9cff',
    active: '#3575ff',
    activeHover: '#b5dcff',
    accent: '#3575ff',
    chevron: '#a1a1aa',
    optionBorder: '#71717a',
  },
} as const satisfies Record<Scheme, NavColors>;

/** Prod's dark field edge: the one shade its override sheet painted that the zinc ramp lacks. */
export const FIELD_EDGE_DARK = '#667085';

/** Prod's header buttons (Filters, Reset, Export): white on the light card edge, #09090b on #64748b in dark. */
export const TOOLBAR = {
  light: { background: '{surface.0}', borderColor: '{surface.300}' },
  dark: { background: '{surface.900}', borderColor: '{surface.500}' },
} as const satisfies Record<Scheme, Record<'background' | 'borderColor', `{surface.${number}}`>>;

export interface TintTokens {
  readonly background: TokenRef;
  readonly borderColor: TokenRef;
  readonly color: TokenRef;
}

const TINT: Record<Scheme, (family: Family) => TintTokens> = {
  light: (f) => ({
    background: tokenRef(f, 100),
    borderColor: tokenRef(f, 200),
    color: tokenRef(f, 700),
  }),
  dark: (f) => ({
    background: tokenRef(f, 950),
    borderColor: tokenRef(f, 700),
    color: tokenRef(f, 300),
  }),
};

/** Surface tint and ink for one severity; toast and tag share it. */
export const tint = (family: Family, scheme: Scheme): TintTokens => TINT[scheme](family);

export interface BannerTone {
  readonly background: string;
  readonly color: string;
}

/**
 * Prod's message banners, main's semantic badge surfaces: info on blue.200 in light, every
 * severity a 20% wash under a 400 ink in dark. The banner's outline takes the ink.
 */
export const BANNER: Record<Scheme, Record<SeverityFamily, BannerTone>> = {
  light: {
    green: { background: '{green.100}', color: '{green.700}' },
    blue: { background: '{blue.200}', color: '{blue.700}' },
    orange: { background: '{orange.100}', color: '{orange.700}' },
    red: { background: '{red.100}', color: '{red.700}' },
  },
  dark: {
    green: { background: 'rgba(16, 185, 129, 0.2)', color: '{emerald.400}' },
    blue: { background: 'rgba(59, 130, 246, 0.2)', color: '{blue.400}' },
    orange: { background: 'rgba(245, 158, 11, 0.2)', color: '{amber.400}' },
    red: { background: 'rgba(239, 68, 68, 0.2)', color: '{red.400}' },
  },
};

export interface SeverityButtonTokens {
  readonly background: TokenRef;
  readonly hoverBackground: TokenRef;
  readonly activeBackground: TokenRef;
  readonly borderColor: TokenRef;
  readonly hoverBorderColor: TokenRef;
  readonly activeBorderColor: TokenRef;
  readonly color: string;
  readonly hoverColor: string;
  readonly activeColor: string;
  readonly focusRing: { readonly color: TokenRef };
}

/** Fill steps as [rest, hover, active]; the border tracks the fill and the focus ring tracks rest. */
const filledButton = (
  family: Family,
  [rest, hover, active]: readonly [Step, Step, Step],
  ink: string
): SeverityButtonTokens => ({
  background: tokenRef(family, rest),
  hoverBackground: tokenRef(family, hover),
  activeBackground: tokenRef(family, active),
  borderColor: tokenRef(family, rest),
  hoverBorderColor: tokenRef(family, hover),
  activeBorderColor: tokenRef(family, active),
  color: ink,
  hoverColor: ink,
  activeColor: ink,
  focusRing: { color: tokenRef(family, rest) },
});

const SEVERITY_BUTTON: Record<Scheme, (family: Family) => SeverityButtonTokens> = {
  light: (f) => filledButton(f, [700, 800, 900], '#ffffff'),
  dark: (f) => filledButton(f, [400, 300, 200], tokenRef(f, 950)),
};

/** Solid severity button: light fills darken on hover/active, dark fills lighten. */
export const severityButton = (family: Family, scheme: Scheme): SeverityButtonTokens =>
  SEVERITY_BUTTON[scheme](family);

/** `color-mix(in srgb, {family.step}, transparent N%)` — Aura's own dark wash shape. */
const wash = (family: Family, step: Step, transparentPct: 84 | 96): string =>
  `color-mix(in srgb, ${tokenRef(family, step)}, transparent ${transparentPct}%)`;

export interface OutlinedTokens {
  readonly color: TokenRef;
  readonly borderColor: TokenRef;
  readonly hoverBackground: string;
  readonly activeBackground: string;
}

export interface TextButtonTokens {
  readonly color: TokenRef;
  readonly hoverBackground: string;
  readonly activeBackground: string;
}

const OUTLINED: Record<Scheme, (family: Family) => OutlinedTokens> = {
  light: (f) => ({
    color: tokenRef(f, 700),
    borderColor: tokenRef(f, 200),
    hoverBackground: tokenRef(f, 50),
    activeBackground: tokenRef(f, 100),
  }),
  dark: (f) => ({
    color: tokenRef(f, 400),
    borderColor: tokenRef(f, 700),
    hoverBackground: wash(f, 400, 96),
    activeBackground: wash(f, 400, 84),
  }),
};

const TEXT_BUTTON: Record<Scheme, (family: Family) => TextButtonTokens> = {
  light: (f) => ({
    color: tokenRef(f, 700),
    hoverBackground: tokenRef(f, 50),
    activeBackground: tokenRef(f, 100),
  }),
  dark: (f) => ({
    color: tokenRef(f, 400),
    hoverBackground: wash(f, 400, 96),
    activeBackground: wash(f, 400, 84),
  }),
};

/** Outlined severity button: Aura's border steps (200 / 700) under the 700 / 400 ink. */
export const severityOutlined = (family: Family, scheme: Scheme): OutlinedTokens =>
  OUTLINED[scheme](family);

/** Text severity button: the same ink, Aura's wash shape for hover and active. */
export const severityText = (family: Family, scheme: Scheme): TextButtonTokens =>
  TEXT_BUTTON[scheme](family);
